/**
 * Project naming convention — shared helpers.
 *
 * Canonical display name format:
 *   `{projectNumber} {clientLabel} {descriptor}`   →   `26-014 Dixson Pavilion`
 *
 * The only dash in the rendered name belongs to the project number itself
 * (`YY-NNN`); component separators are single spaces so the eye reads the
 * line as one tidy phrase rather than three competing dashes.
 *
 * `projectNumber` is immutable once assigned. `clientLabel` and `descriptor`
 * are editable by the user and propagate via the `onEnhancedDealRenamed`
 * Cloud Function trigger to every projection (TimberProject, ClientPortal
 * project entries, ProposalBuilderData, denormalized matchedDealTitle caches,
 * and eventually the QuickBooks sub-customer).
 *
 * During the migration window, legacy `title` is kept as a mirror so older
 * read paths keep working. New code should call `getProjectDisplayName`
 * exclusively.
 */

import type { CRMUser, EnhancedDeal, Lead, TimberProject } from '../types';

/** Values for the shared project naming form ({@link NameProjectFields}). */
export interface NameProjectFieldsValue {
  projectNumber: string;
  clientLabel: string;
  descriptor: string;
}

/**
 * Suggested values for the `descriptor` combobox. Free-text override is
 * always allowed; this list seeds autocomplete and analytics groupings.
 */
export const DESCRIPTOR_SUGGESTIONS: readonly string[] = [
  'Pavilion',
  'Barn',
  'Porch',
  'Pergola',
  'Lake House',
  'Carport',
  'Outdoor Kitchen',
  'Custom Frame',
  'Other',
];

/**
 * Normalize a descriptor input — trims whitespace and Title-Cases the value
 * if it matches a known suggestion (case-insensitive). Free-text is preserved
 * as the user typed it.
 */
export function normalizeDescriptor(input: string | undefined | null): string {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  const match = DESCRIPTOR_SUGGESTIONS.find(s => s.toLowerCase() === trimmed.toLowerCase());
  return match || trimmed;
}

/**
 * Strict format check for a project number. Accepts both:
 *   - Legacy year-prefixed form: `YY-NNN`  (e.g. `26-014`)
 *   - Modern monotonic global form: `#NNNN` or longer  (e.g. `#0048`, `#10523`)
 *
 * Both formats coexist in the system: legacy YY-NNN numbers were minted by
 * the original year-counter implementation and remain valid forever; new
 * assignments come from the global counter introduced with the legacy-rename
 * wizard.
 */
const PROJECT_NUMBER_LEGACY_RE = /^\d{2}-\d{3}$/;
const PROJECT_NUMBER_GLOBAL_RE = /^#?\d{3,6}$/;
export function isProjectNumber(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return PROJECT_NUMBER_LEGACY_RE.test(value) || PROJECT_NUMBER_GLOBAL_RE.test(value);
}

/**
 * Strip the leading project-number prefix from a string when present. Handles
 * both `YY-NNN ` and `#NNNN ` prefix forms. Used by display helpers that show
 * the number separately from the rest of the title.
 */
function stripLeadingProjectNumber(s: string): string {
  return s.replace(/^(\d{2}-\d{3}|#\d{3,6})\s+/, '').trim();
}

/**
 * Compute the canonical display name for a deal. If the new naming fields
 * are missing (deal hasn't been migrated yet), falls back to the legacy
 * `title`. NEVER throws — always returns a non-empty string.
 */
export function getProjectDisplayName(
  deal: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'>
    | null
    | undefined,
): string {
  if (!deal) return '';
  const num = (deal.projectNumber || '').trim();
  const label = (deal.clientLabel || '').trim();
  const desc = (deal.descriptor || '').trim();
  // Label/descriptor are the live project name. A missing project number must
  // not fall through to a stale legacy title ("Old Name - Project").
  if (label || desc) {
    return [num, label, desc].filter(Boolean).join(' ').trim();
  }
  return (deal.title || '').trim();
}

/**
 * Same as {@link getProjectDisplayName} but with the project number stripped
 * from the front. Useful in UIs that show the number separately (e.g. as a
 * meta-line tag) and want to avoid printing it twice. For migrated deals
 * returns "{clientLabel} {descriptor}". For legacy deals, strips a leading
 * "YY-NNN " prefix from the title when present.
 */
export function getProjectDisplayNameWithoutNumber(
  deal: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'>
    | null
    | undefined,
): string {
  if (!deal) return '';
  const label = (deal.clientLabel || '').trim();
  const desc = (deal.descriptor || '').trim();
  if (label || desc) {
    return [label, desc].filter(Boolean).join(' ').trim();
  }
  return stripLeadingProjectNumber(deal.title || '');
}

/**
 * True when a deal hasn't yet been migrated to the canonical naming triple.
 *
 * Migrated = has a project number AND at least one of clientLabel/descriptor.
 * (We intentionally treat "has a number but no label/descriptor" as legacy
 * because the display can't be assembled without those fields — the rename
 * wizard should still pick the deal up.)
 *
 * Used by:
 *   - ClientPortalManagement to switch save behaviour between the legacy
 *     "First Last - Project" template and the canonical naming triple.
 */
export function isLegacyDealName(
  deal: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor'> | null | undefined,
): boolean {
  if (!deal) return false;
  const num = (deal.projectNumber || '').trim();
  const label = (deal.clientLabel || '').trim();
  const desc = (deal.descriptor || '').trim();
  // Has the full triple → migrated.
  if (num && (label || desc)) return false;
  return true;
}

/**
 * Build a sensible default {@link NameProjectFieldsValue} for a legacy deal
 * that's about to be passed through the rename wizard. The returned value
 * has `projectNumber` empty — the wizard decides whether to mint one from
 * the global counter (and the caller can opt out per-deal).
 *
 * Seed precedence:
 *   - clientLabel: existing deal.clientLabel → defaultClientLabelFromLead
 *                  → parseLegacyTitle(deal.title).label
 *   - descriptor:  existing deal.descriptor → parseLegacyTitle(deal.title).descriptor
 *                  → defaultDescriptorFromProjectType(lead.projectType)
 */
export function defaultNamingFromLegacyDeal(
  deal: Pick<EnhancedDeal, 'clientLabel' | 'descriptor' | 'title'> | null | undefined,
  lead: Pick<Lead, 'firstName' | 'lastName' | 'company' | 'projectType'> | null | undefined,
): NameProjectFieldsValue {
  const existingLabel = (deal?.clientLabel || '').trim();
  const existingDesc = (deal?.descriptor || '').trim();
  const parsed = parseLegacyTitle(deal?.title || '');

  const clientLabel =
    existingLabel
    || defaultClientLabelFromLead(lead || null)
    || parsed.label
    || '';

  const descriptor =
    existingDesc
    || parsed.descriptor
    || defaultDescriptorFromProjectType(lead?.projectType || '')
    || '';

  return {
    projectNumber: '',
    clientLabel,
    descriptor,
  };
}

/**
 * Derive a default `clientLabel` from a Lead. Prefers company for commercial
 * leads (when company is present), otherwise the lead's last name.
 */
export function defaultClientLabelFromLead(lead: Pick<Lead, 'firstName' | 'lastName' | 'company'> | null | undefined): string {
  if (!lead) return '';
  const company = (lead.company || '').trim();
  if (company) return company;
  const last = (lead.lastName || '').trim();
  if (last) return last;
  return (lead.firstName || '').trim();
}

/**
 * Derive a default `clientLabel` from a contact-name string (used when a
 * deal exists without a linked Lead). Returns the last whitespace-separated
 * token, falling back to the entire string.
 */
export function defaultClientLabelFromContactName(contactName: string | undefined | null): string {
  const trimmed = (contactName || '').trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  return parts[parts.length - 1] || trimmed;
}

/**
 * Derive a default `descriptor` from a lead's `projectType` value, normalized
 * to a known suggestion when possible.
 */
export function defaultDescriptorFromProjectType(projectType: string | undefined | null): string {
  const raw = (projectType || '').trim();
  if (!raw) return '';
  // Common mappings from existing lead projectType values to canonical descriptors.
  const lower = raw.toLowerCase();
  if (lower.includes('pavilion')) return 'Pavilion';
  if (lower.includes('barn')) return 'Barn';
  if (lower.includes('porch')) return 'Porch';
  if (lower.includes('pergola')) return 'Pergola';
  if (lower.includes('carport')) return 'Carport';
  if (lower.includes('lake house') || lower.includes('lakehouse')) return 'Lake House';
  if (lower.includes('outdoor kitchen')) return 'Outdoor Kitchen';
  if (lower.includes('custom timber') || lower.includes('custom frame')) return 'Custom Frame';
  return normalizeDescriptor(raw);
}

/**
 * Best-effort split of a legacy title `"{First} {Last} - {Descriptor}"` into
 * its label + descriptor parts. Used by the migration script when no Lead
 * link is available.
 */
export function parseLegacyTitle(title: string | undefined | null): {
  label: string;
  descriptor: string;
} {
  const t = (title || '').trim();
  if (!t) return { label: '', descriptor: '' };
  const dashIdx = t.indexOf(' - ');
  if (dashIdx === -1) {
    return { label: defaultClientLabelFromContactName(t), descriptor: '' };
  }
  const left = t.slice(0, dashIdx).trim();
  const right = t.slice(dashIdx + 3).trim();
  return {
    label: defaultClientLabelFromContactName(left),
    descriptor: defaultDescriptorFromProjectType(right),
  };
}

/**
 * One-line title for architect/engineer portal project cards — aligns with
 * Project Hub (# + descriptor). If `deal.projectNumber` is empty, a leading
 * `#NNNN` / `YY-NNN` on `deal.title` is used so rows match the priority list.
 */
export function getPortalProjectCardTitle(
  project: Pick<TimberProject, 'name'>,
  deal: Pick<EnhancedDeal, 'projectNumber' | 'descriptor' | 'title'> | undefined,
): string {
  const title = (deal?.title || '').trim();
  let rawNum = (deal?.projectNumber || '').trim();
  if (!rawNum && title) {
    const m = title.match(/^(#\d{3,6}|\d{2}-\d{3})(?=\s|\s*-|$)/);
    if (m) rawNum = m[1];
  }
  const numberPart = rawNum ? (rawNum.startsWith('#') ? rawNum : `#${rawNum}`) : '';

  let descriptor = (deal?.descriptor || '').trim();
  if (!descriptor && title) {
    descriptor = parseLegacyTitle(title).descriptor.trim();
  }
  if (!descriptor && title && numberPart) {
    descriptor = title.replace(/^(#\d{3,6}|\d{2}-\d{3})\s*(-\s*)?/, '').trim();
  }
  if (!descriptor) descriptor = (project.name || '').trim();

  if (numberPart && descriptor) return `${numberPart} ${descriptor}`;
  if (numberPart) return numberPart;
  return descriptor || project.name || 'Project';
}

/** Subtle project line for the task detail slide-over header (Project Hub + portals). */
export interface TaskDetailPanelProjectContext {
  projectNumberDisplay: string;
  descriptor: string;
  projectManagerDisplay: string;
}

/**
 * Build display strings for the task detail slide-over header — mirrors Project Hub
 * number formatting and descriptor fallbacks; resolves PM uid via `users`.
 */
export function getTaskDetailPanelProjectContext(
  project: Pick<TimberProject, 'name' | 'projectManager'> | null | undefined,
  deal: Pick<EnhancedDeal, 'projectNumber' | 'descriptor' | 'title'> | null | undefined,
  users: Pick<CRMUser, 'uid' | 'displayName'>[],
): TaskDetailPanelProjectContext {
  const headerNumber = (deal?.projectNumber || '').trim();
  const projectNumberDisplay = headerNumber
    ? (headerNumber.startsWith('#') ? headerNumber : `#${headerNumber}`)
    : '';

  const descriptor = (
    (deal?.descriptor || '').trim()
    || parseLegacyTitle(deal?.title || '').descriptor
    || (project?.name || '').trim()
  ).trim();

  let projectManagerDisplay = '';
  const pmUid = (project?.projectManager || '').trim();
  if (pmUid) {
    const u = users.find((x) => x.uid === pmUid);
    projectManagerDisplay = (u?.displayName || '').trim() || `${pmUid.slice(0, 8)}…`;
  }

  return { projectNumberDisplay, descriptor, projectManagerDisplay };
}
